import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { MeshReflectorMaterial } from '@react-three/drei'
import { GANGPAD, HAL, prng, useIndeling } from './indeling'
import { useMaterialen, useObjecten, useOppervlak } from '../scene/store'
import { useMerkTokens } from '../kiosk/useMerkTokens'
import { useOppervlakken } from './useOppervlakken'
import { bandensporen, olievlek, verfSlijtage } from './texturen'

/**
 * Eén betontegel is 3 m in het echt.
 *
 * Kleiner dan je zou denken, en dat is een bewuste ruil: bij 512² geeft 3 m
 * 5,9 mm per texel, zo goed als hetzelfde als 1024² op 4,5 m, voor een kwart
 * van de generatietijd. Herhaling op 3 m valt niet op omdat er niets in de
 * tegel zit dat gróter is dan de tegel — de grootschalige variatie komt van de
 * rijbanen, krimpvoegen, vlekken en vuilwolken hieronder, en die staan in
 * wereldcoördinaten.
 */
const TEGEL = 3
/** Eén tegel belijningsverf is 2 m lijn. */
const VERF_TEGEL = 2
/** Eén tegel bandenspoor is 6 m. */
const SPOOR_TEGEL = 6

function vul(mesh: THREE.InstancedMesh | null, m: THREE.Matrix4[]) {
  if (!mesh) return
  m.forEach((mat, i) => mesh.setMatrixAt(i, mat))
  mesh.count = m.length
  mesh.instanceMatrix.needsUpdate = true
  mesh.computeBoundingSphere()
}

/** Decals liggen boven de vloer zonder z-fighting en zonder te zweven. */
const DECAL = { polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1, depthWrite: false, transparent: true }

/**
 * Vloer met belijning, krimpvoegen, slijtsporen in de rijlijnen, een zebrapad,
 * olievlekken en vuil. De markeringen staan in hi-vis amber — de
 * signatuurkleur van het systeem is toevallig ook magazijn-hi-vis.
 *
 * Twee dingen maken hier het verschil tussen "vloer" en "grijs vlak":
 *
 *  1. **Gepolijst beton met reflectie.** Het grootste vlak in beeld, en de
 *     enige plek waar licht twee keer in beeld komt. Dat laat een render duur
 *     lijken — niet meer detail, maar dezelfde lampen nog eens, zacht, in de
 *     vloer.
 *  2. **Gebruik.** Een nieuwe vloer bestaat niet. Belijning bladdert af waar de
 *     wielen eroverheen gaan, de rijbanen worden donker van rubber, onder een
 *     laadpaal ligt een vlek. Tot nu toe was de belijning onbelicht (basic,
 *     `toneMapped: false`): lijnen die gloeiden in het donker en geen schaduw
 *     kregen, en het zebrapad las als een rij witte tl-balken. Nu is het verf,
 *     die het licht van de hal krijgt zoals de vloer eronder.
 *
 * De reflectie kost een tweede scènerender per frame en is daarom de eerste
 * schuif die uit gaat als de beurs-pc hem niet trekt (`oppervlak.vloerReflectie`).
 */
export function Vloer() {
  const ind = useIndeling()
  const mat = useMaterialen()
  const obj = useObjecten()
  const opp = useOppervlak()
  const tokens = useMerkTokens()
  const set = useOppervlakken()

  const strepen = useRef<THREE.InstancedMesh>(null)
  const voegen = useRef<THREE.InstancedMesh>(null)
  const sporen = useRef<THREE.InstancedMesh>(null)
  const zebra = useRef<THREE.InstancedMesh>(null)
  const vlekken = useRef<THREE.InstancedMesh>(null)
  const vuil = useRef<THREE.InstancedMesh>(null)

  const vlak: [number, number] = [HAL.breedte * 1.8, HAL.diepte * 1.8]

  // De vloer is de enige gebruiker van de betonset, dus de herhaling mag direct
  // op de gedeelde maps. Een kopie per mesh zou hier alleen VRAM kosten.
  useLayoutEffect(() => {
    if (!set) return
    const x = vlak[0] / TEGEL
    const y = vlak[1] / TEGEL
    for (const t of [set.beton.map, set.beton.roughnessMap, set.beton.normalMap]) {
      t.repeat.set(x, y)
      t.needsUpdate = true
    }
  }, [set, vlak[0], vlak[1]])

  const lengte = HAL.breedte * 0.92

  // Eigen texturen per toepassing: de lijnen, het zebrapad en de sporen hebben
  // elk een andere herhaling, en `repeat` zit op de textuur. Een kloon deelt
  // de pixels (één upload), alleen de transformatie is eigen.
  const decals = useMemo(() => {
    const seed = ind.cfg.seed
    const verf = verfSlijtage(seed + 5)
    const streep = verf.clone()
    streep.repeat.set(lengte / VERF_TEGEL, 1)
    const zeb = verf.clone()
    zeb.repeat.set(1.2, 1)
    zeb.rotation = Math.PI / 2
    zeb.center.set(0.5, 0.5)
    const spoor = bandensporen(seed + 9)
    spoor.repeat.set((lengte * 0.96) / SPOOR_TEGEL, 1)
    const vlek = olievlek(seed + 13)
    return { verf, streep, zeb, spoor, vlek }
  }, [ind.cfg.seed, lengte])

  useEffect(() => () => {
    for (const t of Object.values(decals)) t.dispose()
  }, [decals])

  const opbouw = useMemo(() => {
    const d = new THREE.Object3D()
    const streepM: THREE.Matrix4[] = []
    const voegM: THREE.Matrix4[] = []
    const spoorM: THREE.Matrix4[] = []
    const zebraM: THREE.Matrix4[] = []
    const vlekM: THREE.Matrix4[] = []
    const vuilM: THREE.Matrix4[] = []
    const zet = (a: THREE.Matrix4[]) => { d.updateMatrix(); a.push(d.matrix.clone()) }
    const rnd = prng(ind.cfg.seed + 606)

    for (let i = 0; i < ind.cfg.gangpaden; i++) {
      const z = ind.gangpadZ(i)
      // Twee belijningen per gangpad
      for (const dz of [-GANGPAD.breedte / 2 + 0.18, GANGPAD.breedte / 2 - 0.18]) {
        d.position.set(0, 0.004, z + dz)
        d.rotation.set(-Math.PI / 2, 0, 0)
        d.scale.set(lengte, 0.1, 1)
        zet(streepM)
      }
      // Rijbaan: waar de wielen van de trucks lopen, donker van rubber.
      d.position.set(0, 0.003, z)
      d.rotation.set(-Math.PI / 2, 0, 0)
      d.scale.set(lengte * 0.96, 1.5, 1)
      zet(spoorM)
    }

    // Krimpvoegen in een raster van 6 meter
    for (let x = -HAL.breedte * 0.85; x <= HAL.breedte * 0.85; x += 6) {
      d.position.set(x, 0.002, 0)
      d.rotation.set(-Math.PI / 2, 0, 0)
      d.scale.set(0.012, HAL.diepte * 1.7, 1)
      zet(voegM)
    }
    for (let z = -HAL.diepte * 0.85; z <= HAL.diepte * 0.85; z += 6) {
      d.position.set(0, 0.002, z)
      d.rotation.set(-Math.PI / 2, 0, 0)
      d.scale.set(HAL.breedte * 1.7, 0.012, 1)
      zet(voegM)
    }

    // Zebrapad dwars over de hal aan de voorzijde
    const zZebra = ind.blokDiepte + 6
    for (let i = 0; i < 26; i++) {
      d.position.set(-26 + i * 2.1, 0.005, zZebra)
      d.rotation.set(-Math.PI / 2, 0, 0)
      d.scale.set(0.85, 2.4, 1)
      zet(zebraM)
    }

    // Vlekken: waar gewerkt wordt. Kopse kanten van de gangpaden (daar keert
    // en wacht een truck), de laadstraat en de dockdeuren.
    const halveStelling = (ind.cfg.bays * 2.7) / 2
    const vlekOp = (x: number, z: number, maat: number) => {
      d.position.set(x, 0.006, z)
      d.rotation.set(-Math.PI / 2, 0, rnd() * Math.PI * 2)
      const s = maat * (0.6 + rnd() * 0.8)
      d.scale.set(s, s * (0.7 + rnd() * 0.5), 1)
      zet(vlekM)
    }
    for (let i = 0; i < ind.cfg.gangpaden; i++) {
      for (const kant of [-1, 1]) {
        const n = 1 + Math.floor(rnd() * 3)
        for (let k = 0; k < n; k++) {
          vlekOp(kant * (halveStelling + 1 + rnd() * 5), ind.gangpadZ(i) + (rnd() - 0.5) * 2.6, 0.5)
        }
      }
      // Een enkele vlek midden in het gangpad.
      if (rnd() > 0.4) vlekOp((rnd() - 0.5) * halveStelling * 1.6, ind.gangpadZ(i) + (rnd() - 0.5) * 1.2, 0.35)
    }
    for (let k = 0; k < 8; k++) vlekOp(-HAL.breedte * 0.42 + (rnd() - 0.5) * 2, ind.blokDiepte + 4 + rnd() * 14, 0.6)
    for (let k = 0; k < 10; k++) vlekOp((rnd() - 0.5) * 44, -HAL.diepte * 0.8 + rnd() * 5, 0.7)

    // Vuilwolken: dezelfde vlektextuur, maar meters groot en bijna doorzichtig.
    // Dit is de laagfrequente variatie die niet in de betontegel mag zitten
    // omdat hij daar om de drie meter zou herhalen.
    for (let k = 0; k < 70; k++) {
      d.position.set((rnd() - 0.5) * HAL.breedte * 1.5, 0.001, (rnd() - 0.5) * HAL.diepte * 1.5)
      d.rotation.set(-Math.PI / 2, 0, rnd() * Math.PI * 2)
      const s = 5 + rnd() * 9
      d.scale.set(s, s * (0.5 + rnd() * 0.6), 1)
      zet(vuilM)
    }

    return { streepM, voegM, spoorM, zebraM, vlekM, vuilM }
  }, [ind, lengte])

  useLayoutEffect(() => {
    vul(strepen.current, opbouw.streepM)
    vul(voegen.current, opbouw.voegM)
    vul(sporen.current, opbouw.spoorM)
    vul(zebra.current, opbouw.zebraM)
    vul(vlekken.current, opbouw.vlekM)
    vul(vuil.current, opbouw.vuilM)
  }, [opbouw, obj.vloermarkering])

  // Textuurprops los, want ze gaan naar twee verschillende materialen.
  const betonProps = set
    ? {
        map: set.beton.map,
        roughnessMap: set.beton.roughnessMap,
        normalMap: set.beton.normalMap,
        normalScale: new THREE.Vector2(opp.relief, opp.relief),
      }
    : {}

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={vlak} />
        {opp.vloerReflectie ? (
          <MeshReflectorMaterial
            color={mat.vloer}
            roughness={0.62}
            metalness={0.12}
            {...betonProps}
            resolution={opp.reflectieResolutie}
            mirror={0}
            mixStrength={opp.reflectieSterkte * 3.2}
            mixBlur={1}
            blur={[opp.reflectieVervaging * 700, opp.reflectieVervaging * 220]}
            mixContrast={1.1}
            // Zonder dieptedrempels spiegelt ook wat ver weg staat scherp mee, en
            // dan wordt de vloer een spiegel in plaats van gepolijst beton.
            depthScale={0.9}
            minDepthThreshold={0.3}
            maxDepthThreshold={1.2}
            depthToBlurRatioBias={0.3}
            reflectorOffset={0.002}
          />
        ) : (
          <meshStandardMaterial color={mat.vloer} roughness={0.62} metalness={0.12} {...betonProps} />
        )}
      </mesh>

      {/* Vuilwolken: laagfrequent, meters groot, bijna doorzichtig. */}
      <instancedMesh ref={vuil} args={[undefined, undefined, opbouw.vuilM.length]} receiveShadow renderOrder={1}>
        <planeGeometry args={[1, 1]} />
        <meshStandardMaterial
          color="#0b0c0e" alphaMap={decals.vlek} opacity={0.22} roughness={0.8} metalness={0}
          {...DECAL}
        />
      </instancedMesh>

      {/* Rijbanen: donkerder en gladder dan de rest — rubber en gepolijst door
          het verkeer. Doorschijnend, want een spoor dempt de spiegeling van de
          armaturen, het snijdt hem niet weg. */}
      <instancedMesh ref={sporen} args={[undefined, undefined, opbouw.spoorM.length]} receiveShadow renderOrder={1}>
        <planeGeometry args={[1, 1]} />
        <meshStandardMaterial
          color="#08090b" alphaMap={decals.spoor} opacity={0.55} roughness={0.32} metalness={0.2}
          {...DECAL}
        />
      </instancedMesh>

      <instancedMesh ref={voegen} args={[undefined, undefined, opbouw.voegM.length]} receiveShadow renderOrder={1}>
        <planeGeometry args={[1, 1]} />
        <meshStandardMaterial color="#07080a" roughness={0.95} opacity={0.85} {...DECAL} />
      </instancedMesh>

      <instancedMesh ref={vlekken} args={[undefined, undefined, opbouw.vlekM.length]} receiveShadow renderOrder={2}>
        <planeGeometry args={[1, 1]} />
        {/* Olie is donker én glad: hij glanst in het licht van de armaturen. */}
        <meshStandardMaterial
          color="#050506" alphaMap={decals.vlek} opacity={0.8} roughness={0.12} metalness={0.3}
          {...DECAL}
        />
      </instancedMesh>

      {obj.vloermarkering && (
        <>
          <instancedMesh ref={strepen} args={[undefined, undefined, opbouw.streepM.length]} receiveShadow renderOrder={3}>
            <planeGeometry args={[1, 1]} />
            <meshStandardMaterial
              color={tokens.hi} map={decals.streep} roughness={0.55} metalness={0}
              alphaTest={0.02} {...DECAL}
            />
          </instancedMesh>

          <instancedMesh ref={zebra} args={[undefined, undefined, opbouw.zebraM.length]} receiveShadow renderOrder={3}>
            <planeGeometry args={[1, 1]} />
            <meshStandardMaterial
              color="#d9dcdf" map={decals.zeb} roughness={0.6} metalness={0}
              alphaTest={0.02} {...DECAL}
            />
          </instancedMesh>
        </>
      )}
    </group>
  )
}
